import { AlertTriangle, Check, Clock } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import type { DisruptionPnrGroupRow } from '@/lib/disruptions/types';
import { FieldLabel } from './airport-ui';

// The Hotel tab's side card: WHY this passenger gets (or does not get) a room —
// the overnight verdict in plain words, what the hotel does not cover (meals
// ride the card), whether a ride to it exists, and the declared accommodation
// outcome. Reads the same verdict the hotel card and the step board use, so the
// three never disagree. Presentational.

export function HotelWhyCard({
  group,
  needs,
  reserved,
  sameDay,
  hubNight,
  hasCard,
  transportArranged,
  transportOpen = true,
  hasFlight,
  careOnly,
  stayingHome,
}: {
  group: DisruptionPnrGroupRow;
  /** Overnight verdict — a hotel is needed. */
  needs: boolean;
  /** A live (non-terminal) reservation exists — the hotel is in play even when
   *  the engine's verdict said voucher/none (an operator can always book one). */
  reserved: boolean;
  /** Same-day re-accommodation — no overnight. */
  sameDay: boolean;
  /** The night falls at the connection hub, after the first leg. */
  hubNight: boolean;
  /** A care card / meal entitlement exists — meals are not the hotel's. */
  hasCard: boolean;
  /** A ride exists for this PNR (live trip or shuttle seat). */
  transportArranged: boolean;
  /** The PNR has somewhere to drive to (a CONFIRMED hotel or the stay-home).
   *  Until then a missing transfer is not late — it is not possible yet. */
  transportOpen?: boolean;
  /** A re-accommodation flight actually EXISTS (selected/assigned — the same
   *  signal the flight step uses). The flight-derived overnight copy ("departs
   *  on the next calendar day") may render ONLY when this is true — saying it
   *  for an awaiting-flight PNR was the lie an operator caught live. */
  hasFlight: boolean;
  /** Care-only case: the stay is an offering, phrased as such (GOAL 1). */
  careOnly?: boolean;
  /** Operator-declared stay-home — the honest "why no hotel". */
  stayingHome?: boolean;
}) {
  const { t } = useTranslation('pnr');
  const station = group.overnightStation ?? group.origin;
  const inPlay = needs || reserved;
  const voucher = group.accommodationKind === 'MEAL_VOUCHER' && !needs;
  const outcome = group.accommodationOutcome?.outcome;

  let reason: React.ReactNode;
  if (stayingHome) reason = t('hotelWhy.stayHome');
  else if (needs && hasFlight && hubNight) reason = t('hotelWhy.overnightHub', { station });
  else if (needs && hasFlight)
    reason = (
      <Trans t={t} i18nKey="hotelWhy.overnight" values={{ station }} components={{ 1: <strong /> }} />
    );
  else if (needs)
    // needs=true with NO flight chosen: the honest reason — the stay bridges the
    // passenger until rebooking resolves; on a care-only case, the GOAL-1
    // optional phrasing instead.
    reason = careOnly ? t('hotelWhy.careOnly') : t('hotelWhy.noFlightYet');
  else if (reserved) reason = t('hotelWhy.reservedAnyway');
  else if (voucher) reason = t('hotelWhy.voucher');
  else if (sameDay && hasFlight) reason = t('hotelWhy.sameDay');
  else if (careOnly) reason = t('hotelWhy.careOnly');
  else reason = t('hotelWhy.pending');

  return (
    <section className="rounded-lg border border-border p-4" data-testid="hotel-why">
      <FieldLabel className="flex items-center gap-1.5">
        <Clock className="h-3.5 w-3.5" aria-hidden />
        {inPlay ? t('hotelWhy.title') : t('hotelWhy.titleNone')}
      </FieldLabel>
      <p className="mt-3 text-sm leading-5">{reason}</p>
      {inPlay ? (
        <ul className="mt-3 space-y-2 text-[13px]">
          {hasCard ? (
            <li className="flex gap-2">
              <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-700 dark:text-emerald-400" aria-hidden />
              <span>{t('hotelWhy.mealsOnCard')}</span>
            </li>
          ) : null}
          <li className="flex gap-2" data-testid="hotel-why-transport">
            {transportArranged ? (
              <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-700 dark:text-emerald-400" aria-hidden />
            ) : transportOpen ? (
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-700 dark:text-amber-400" aria-hidden />
            ) : (
              <Clock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
            )}
            <span>
              {transportArranged
                ? t('hotelWhy.transportOk')
                : transportOpen
                  ? t('hotelWhy.transportMissing')
                  : t('hotelWhy.transportAwaiting')}
            </span>
          </li>
        </ul>
      ) : null}
      <div className="mt-4 border-t border-border pt-3">
        <FieldLabel>{t('hotelWhy.outcomeTitle')}</FieldLabel>
        <p className="mt-1.5 text-[13px] text-muted-foreground" data-testid="hotel-why-outcome">
          {outcome ? t(`hotelWhy.outcome.${outcome}`) : t('hotelWhy.outcomeEngine')}
        </p>
        {group.accommodationOutcome?.note ? (
          <p className="mt-1 text-[13px] text-muted-foreground">{group.accommodationOutcome.note}</p>
        ) : null}
      </div>
    </section>
  );
}
